import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  Input,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { CheckCircleOutlined, LockOutlined, RedoOutlined, SendOutlined, UserOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { useAuth, usePermissions, type AdminRole } from '../../auth/store';
import { Conversation } from './Conversation';
import { UserContextCard } from './UserContextCard';
import { userLabel, userSecondary } from '../../lib/listenerName';
import {
  CATEGORY_COLOR,
  CATEGORY_LABEL,
  dateTime,
  errMsg,
  STATUS_COLOR,
  STATUS_LABEL,
  ADMIN_SUPPORT,
  listKey,
  summaryKey,
  ticketKey,
  type SupportScope,
  type SupportStatus,
  type TicketDetail as Ticket,
} from './api';

interface Operator {
  id: string;
  name: string;
  email: string;
  role: AdminRole;
  isActive: boolean;
}

type Update = { status: SupportStatus } | { assignedAdminId: string | null };

function isClientError(e: unknown) {
  const status = (e as { response?: { status?: number } } | null)?.response?.status;
  return status !== undefined && status >= 400 && status < 500;
}

/** The open support request: conversation, reply box, status/assignment, user context. */
export function TicketDetail({ id, scope = ADMIN_SUPPORT }: { id: string; scope?: SupportScope }) {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const profile = useAuth((s) => s.profile);
  // Reading a ticket, answering it, and changing its status/assignment are
  // three separate permissions — opening the inbox implies none of the others.
  const { can } = usePermissions();
  // An agency answers and closes its own users' requests under one permission;
  // internal notes and assignment are the Dude team's.
  const agency = scope.agency;
  const canReply = agency ? can('agency_portal.support') : can('support.reply');
  const canManage = agency ? can('agency_portal.support') : can('support.manage');
  const isSuperAdmin = !agency && profile?.role === 'super_admin';
  const supportTicketKey = (ticketId: string) => ticketKey(scope, ticketId);
  const [draft, setDraft] = useState('');

  const detail = useQuery({
    queryKey: supportTicketKey(id),
    queryFn: async () => (await api.get<Ticket>(`${scope.base}/tickets/${id}`)).data,
    // A 403 (finance opening a non-payment request) or 404 will not fix
    // itself: don't retry it and stop polling while it is failing.
    retry: (count, e) => !isClientError(e) && count < 1,
    refetchInterval: (query) => (isClientError(query.state.error) ? false : 10_000),
  });

  // Same key + fetcher as the Sub admins page, so the two share a cache.
  const operators = useQuery({
    queryKey: ['admin', 'operators'],
    queryFn: async () => (await api.get<Operator[]>('/admin/operators')).data,
    enabled: isSuperAdmin,
  });

  function refreshLists() {
    void qc.invalidateQueries({ queryKey: listKey(scope) });
    void qc.invalidateQueries({ queryKey: summaryKey(scope) });
  }

  const { mutate: markRead } = useMutation({
    mutationFn: async (ticketId: string) => {
      await api.post(`${scope.base}/tickets/${ticketId}/read`);
    },
    onSuccess: (_data, ticketId) => {
      qc.setQueryData<Ticket>(supportTicketKey(ticketId), (old) => (old ? { ...old, unread: 0 } : old));
      refreshLists();
    },
    onError: (e) => message.error(errMsg(e, 'Could not mark the request as read')),
  });

  // Opening a request with unread user messages marks them read. Keyed on the
  // last activity so a new user message that arrives while it is open is
  // marked too, but a single change is never posted twice (StrictMode runs
  // effects twice in dev).
  const ticket = detail.data;
  const markedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!ticket || ticket.unread <= 0) return;
    const key = `${ticket.id}@${ticket.lastMessageAt}`;
    if (markedFor.current === key) return;
    markedFor.current = key;
    markRead(ticket.id);
  }, [ticket, markRead]);

  const reply = useMutation({
    mutationFn: async (vars: { message: string; internal: boolean }) =>
      (await api.post<Ticket>(`${scope.base}/tickets/${id}/reply`, agency ? { message: vars.message } : vars)).data,
    onSuccess: (data, vars) => {
      qc.setQueryData(supportTicketKey(id), data);
      setDraft('');
      message.success(vars.internal ? 'Internal note added' : 'Reply sent — the user was notified');
      refreshLists();
    },
    onError: (e, vars) =>
      message.error(errMsg(e, vars.internal ? 'Could not add the note' : 'Could not send the reply')),
  });

  const update = useMutation({
    mutationFn: async (body: Update) => (await api.patch<Ticket>(`${scope.base}/tickets/${id}`, body)).data,
    onSuccess: (data, body) => {
      qc.setQueryData(supportTicketKey(id), data);
      if ('status' in body) {
        message.success(body.status === 'resolved' ? 'Marked resolved' : 'Request reopened');
      } else {
        message.success(data.assignedAdmin ? `Assigned to ${data.assignedAdmin.name}` : 'Unassigned');
      }
      refreshLists();
    },
    onError: (e) => message.error(errMsg(e, 'Could not update the request')),
  });

  if (detail.isLoading) {
    return (
      <Card>
        <div style={{ padding: 48, textAlign: 'center' }}>
          <Spin />
        </div>
      </Card>
    );
  }
  if (!ticket) {
    return (
      <Card>
        <Alert type="error" showIcon message={errMsg(detail.error, 'Could not load this support request')} />
      </Card>
    );
  }

  const text = draft.trim();
  const sending = reply.isPending;
  const noText = text.length === 0;
  const send = (internal: boolean) => {
    if (!noText) reply.mutate({ message: text, internal });
  };

  const assignee = ticket.assignedAdmin;
  const assignedToMe = !!assignee && assignee.id === profile?.id;
  const operatorOptions = (operators.data ?? [])
    .filter((o) => o.isActive || o.id === assignee?.id)
    .map((o) => ({ value: o.id, label: `${o.name} · ${o.role}` }));
  if (assignee && !operatorOptions.some((o) => o.value === assignee.id)) {
    operatorOptions.unshift({ value: assignee.id, label: assignee.name });
  }

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} xxl={15}>
        <Card
          title={
            <Space size={8} wrap>
              <span>{userLabel(ticket.user)}</span>
              {userSecondary(ticket.user) && (
                <Typography.Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
                  {userSecondary(ticket.user)}
                </Typography.Text>
              )}
              <Tag color={STATUS_COLOR[ticket.status]}>{STATUS_LABEL[ticket.status]}</Tag>
              <Tag color={CATEGORY_COLOR[ticket.category]}>{CATEGORY_LABEL[ticket.category]}</Tag>
              {ticket.fromListenerMode && <Tag color="magenta">Listener</Tag>}
            </Space>
          }
          extra={
            !canManage ? null : ticket.status === 'resolved' ? (
              <Button
                icon={<RedoOutlined />}
                loading={update.isPending && 'status' in (update.variables ?? {})}
                onClick={() => update.mutate({ status: 'open' })}
              >
                Reopen
              </Button>
            ) : (
              <Popconfirm
                title="Mark this request resolved?"
                description="The user sees a 'resolved' message and can still reply."
                okText="Mark resolved"
                onConfirm={() => update.mutate({ status: 'resolved' })}
              >
                <Button
                  type="primary"
                  ghost
                  icon={<CheckCircleOutlined />}
                  loading={update.isPending && 'status' in (update.variables ?? {})}
                >
                  Mark resolved
                </Button>
              </Popconfirm>
            )
          }
        >
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
              }}
            >
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Request{' '}
                <Typography.Text
                  type="secondary"
                  copyable={{ text: ticket.id }}
                  style={{ fontSize: 12, fontFamily: 'monospace' }}
                >
                  {ticket.id.slice(0, 8)}…
                </Typography.Text>{' '}
                · opened {dateTime(ticket.createdAt)}
                {ticket.firstReplyAt && <> · first reply {dateTime(ticket.firstReplyAt)}</>}
                {ticket.resolvedAt && <> · resolved {dateTime(ticket.resolvedAt)}</>}
              </Typography.Text>

              {!agency && (
              <Space size={8} wrap>
                <UserOutlined style={{ color: '#8B8B8B' }} />
                {isSuperAdmin ? (
                  <Select<string | undefined>
                    allowClear
                    placeholder="Unassigned"
                    value={assignee?.id}
                    loading={operators.isLoading || (update.isPending && 'assignedAdminId' in (update.variables ?? {}))}
                    onChange={(v) => update.mutate({ assignedAdminId: v ?? null })}
                    options={operatorOptions}
                    style={{ minWidth: 200 }}
                    size="small"
                    showSearch
                    optionFilterProp="label"
                  />
                ) : (
                  <>
                    <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                      {assignee ? (assignedToMe ? 'Assigned to you' : `Assigned to ${assignee.name}`) : 'Unassigned'}
                    </Typography.Text>
                    {profile && canManage && (
                      <Button
                        size="small"
                        loading={update.isPending && 'assignedAdminId' in (update.variables ?? {})}
                        onClick={() => update.mutate({ assignedAdminId: assignedToMe ? null : profile.id })}
                      >
                        {assignedToMe ? 'Unassign' : 'Assign to me'}
                      </Button>
                    )}
                  </>
                )}
              </Space>
              )}
            </div>

            <Conversation messages={ticket.messages} userName={userLabel(ticket.user)} />

            {canReply && (
            <Input.TextArea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                  e.preventDefault();
                  send(false);
                }
              }}
              placeholder={agency ? 'Write a reply to the user…' : 'Write a reply to the user, or an internal note for the team…'}
              autoSize={{ minRows: 3, maxRows: 8 }}
              maxLength={4000}
              showCount
              disabled={sending}
            />
            )}
            {canReply && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'space-between', marginTop: 4 }}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {agency
                  ? 'Send reply notifies the user by push (Ctrl+Enter). It reaches them as “Dude Support”.'
                  : 'Send reply notifies the user by push (Ctrl+Enter). Internal notes are only visible to admins.'}
              </Typography.Text>
              <Space wrap>
                {!agency && (
                <Tooltip title={noText ? 'Type a note first' : 'Only admins see internal notes'}>
                  <Button
                    icon={<LockOutlined />}
                    disabled={noText || (sending && !reply.variables?.internal)}
                    loading={sending && reply.variables?.internal}
                    onClick={() => send(true)}
                  >
                    Add internal note
                  </Button>
                </Tooltip>
                )}
                <Tooltip title={noText ? 'Type a reply first' : undefined}>
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    disabled={noText || (sending && reply.variables?.internal)}
                    loading={sending && !reply.variables?.internal}
                    onClick={() => send(false)}
                  >
                    Send reply
                  </Button>
                </Tooltip>
              </Space>
            </div>
            )}
          </Space>
        </Card>
      </Col>
      <Col xs={24} xxl={9}>
        <UserContextCard user={ticket.user} linkUsers={!agency} />
      </Col>
    </Row>
  );
}
